// The root of every V2 surface. McRoot paints the .mc scope (tokens, base and every component rule apply only inside
// it), carries the frozen instant and the capture/demo/reduced-motion flags, owns the portal layer and mounts the
// capture settle signal (settle.ts) only under capture or demo in a dev or capture build. This file names no window
// global of the signal, so a production bundle and its source map carry none (CI's dist grep). It is the only file in src/mc that calls createPortal (McPortal) and the only one that reads the
// clock (once, at mount, when no `now` is given — page.clock freezes it under capture).

import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";
import { installSettle, type Settle, type SettleController, type SettleWindow } from "./settle";

const NO_SETTLE: Settle = { pending() {} };

// ---- context ------------------------------------------------------------------------------------------------------

export interface McContextValue {
  /** The frozen instant, in epoch milliseconds. */
  readonly now: number;
  /** Capture mode: html[data-capture] (or the prop). Motion off, hotspots on (capture builds), no viewer storage. */
  readonly capture: boolean;
  /** Demo mode: hotspots on (dev or capture builds), nothing else. */
  readonly demo: boolean;
  /** True under prefers-reduced-motion or capture: JS-timed motion skips its animation. */
  readonly reducedMotion: boolean;
}

const McContext = createContext<McContextValue | null>(null);
const SettleContext = createContext<Settle>(NO_SETTLE);

/** The portal element of the nearest layer: McRoot's body-level layer, or a McLayerHost's (a gallery cell). */
export const McLayer = createContext<HTMLElement | null>(null);

const FALLBACK: McContextValue = { now: 0, capture: false, demo: false, reducedMotion: false };

/** The root's instant and flags. Outside a McRoot: instant 0, every flag off. */
export function useMc(): McContextValue {
  return useContext(McContext) ?? FALLBACK;
}

/** The registered-work API of the capture settle signal (`settle.pending(promise)`); inert outside capture and demo. */
export function useCaptureSettle(): Settle {
  return useContext(SettleContext);
}

/**
 * Where an overlay portals: the nearest layer element, or null during SSR and before the first effect. With null the
 * overlay renders its open content inline, in place (ground rule 9). Use McPortal rather than createPortal.
 */
export function usePortalTarget(): HTMLElement | null {
  return useContext(McLayer);
}

/** Renders children into the nearest layer, or inline in place when there is none yet (SSR, first render). */
export function McPortal({ children }: { children: ReactNode }) {
  const target = usePortalTarget();
  return target ? createPortal(children, target) : <>{children}</>;
}

/** A nested layer whose element sits inside this host, so overlays opened in it stay in it (gallery cells). */
export function McLayerHost({ children }: { children: ReactNode }) {
  const [element, setElement] = useState<HTMLElement | null>(null);
  return (
    <McLayer.Provider value={element}>
      {children}
      <div className="mc-layer" ref={setElement} />
    </McLayer.Provider>
  );
}

// ---- McRoot -------------------------------------------------------------------------------------------------------

const browser = typeof window !== "undefined" && typeof document !== "undefined";

function toEpoch(value: number | string | Date): number {
  return typeof value === "number" ? value : typeof value === "string" ? Date.parse(value) : value.getTime();
}

export interface McRootProps {
  children?: ReactNode;
  /** The frozen instant every relative time reads. Absent: the clock at mount (frozen by page.clock under capture). */
  now?: number | string | Date | undefined;
  /** Default: html[data-capture] in the browser, false under SSR. */
  capture?: boolean | undefined;
  demo?: boolean | undefined;
  /** mc-* classes for the root element. */
  className?: string | undefined;
}

export function McRoot({ children, now, capture, demo = false, className }: McRootProps) {
  const [mounted] = useState(() => (now === undefined ? (browser ? new Date().getTime() : 0) : toEpoch(now)));
  const instant = now === undefined ? mounted : toEpoch(now);
  const isCapture = capture ?? (browser ? document.documentElement.hasAttribute("data-capture") : false);
  const prefersReduced = browser && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
  const value = useMemo<McContextValue>(
    () => ({ now: instant, capture: isCapture, demo, reducedMotion: isCapture || prefersReduced }),
    [instant, isCapture, demo, prefersReduced],
  );

  // The body-level layer: created after mount, so SSR and the first render portal nothing.
  const [layer, setLayer] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const element = document.createElement("div");
    element.className = "mc mc-layer";
    document.body.appendChild(element);
    setLayer(element);
    return () => {
      element.remove();
      setLayer(null);
    };
  }, []);

  // Children's effects run before this root's, so work registered before the controller exists is kept (and
  // re-registered if StrictMode re-creates the controller) until it settles.
  const controller = useRef<SettleController | null>(null);
  const active = useRef(new Set<PromiseLike<unknown>>());
  const [settle] = useState<Settle>(() => ({
    pending(work) {
      active.current.add(work);
      const done = () => { active.current.delete(work); };
      Promise.resolve(work).then(done, done);
      controller.current?.settle.pending(work);
    },
  }));
  // The guard: the signal exists only in a development or capture build (as the hotspot attributes do), and there only
  // under capture or demo (installSettle). The build test is written inline, as main.tsx writes the gallery's, so a
  // default build folds it to false and drops the branch, the call and settle.ts with it.
  useEffect(() => {
    if (import.meta.env.DEV || import.meta.env.VITE_MC_CAPTURE === "true") {
      const created = installSettle(window as unknown as SettleWindow, { capture: isCapture, demo });
      if (created) {
        controller.current = created;
        for (const work of active.current) created.settle.pending(work);
        return () => {
          created.dispose();
          controller.current = null;
        };
      }
    }
    return undefined;
  }, [isCapture, demo]);
  useEffect(() => {
    controller.current?.commit();
  });

  return (
    <McContext.Provider value={value}>
      <SettleContext.Provider value={settle}>
        <McLayer.Provider value={layer}>
          <div className={cx("mc", className)}>{children}</div>
        </McLayer.Provider>
      </SettleContext.Provider>
    </McContext.Provider>
  );
}
